import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchEgitimTsKayitDurum,
  updateEgitimTsKayitGrup,
  runEgitimTsKayitOlustur,
} from '../services/egitimTsKayitService';
import { searchTechServices } from '../services/techServicesService';
import { decodeHtml } from '../utils/security';
import './EgitimTsKayitPage.css';

const getErrorMessage = (err, fallback) =>
  err?.message || err?.data?.mesaj || fallback;

const tsId = (ts) => String(ts?.id || ts?._id || '');

const tsLabel = (ts) => {
  const ad = decodeHtml(ts?.ad || '');
  const kod = ts?.kod != null ? `#${ts.kod}` : '';
  return kod ? `${ad} ${kod}` : ad;
};

const durumLabel = (durum) => {
  if (durum === 'olusturuldu') return 'Oluşturuldu';
  if (durum === 'atlandi') return 'Zaten var';
  if (durum === 'hata') return 'Hata';
  return durum || '';
};

const EgitimTsKayitPage = () => {
  const { confirm } = useConfirmDialog();
  const [durum, setDurum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [sonuclar, setSonuclar] = useState([]);
  const [arama, setArama] = useState('');
  const [aramaSonuclari, setAramaSonuclari] = useState([]);
  const [aramaAcik, setAramaAcik] = useState(false);
  const searchTimer = useRef(null);

  const loadDurum = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchEgitimTsKayitDurum();
      setDurum(res?.veri || null);
    } catch (e) {
      setError(getErrorMessage(e, 'Durum yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDurum();
  }, [loadDurum]);

  const grup = durum?.teknikServisler || [];
  const grupIds = grup.map(tsId);

  const saveGrup = async (ids) => {
    setSaving(true);
    setError('');
    try {
      const res = await updateEgitimTsKayitGrup(ids);
      setDurum((prev) => ({
        ...(prev || {}),
        teknikServisler: res?.veri?.teknikServisler || [],
      }));
    } catch (e) {
      setError(getErrorMessage(e, 'Grup kaydedilemedi'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (ts) => {
    const id = tsId(ts);
    if (!id || grupIds.includes(id)) {
      setArama('');
      setAramaSonuclari([]);
      setAramaAcik(false);
      return;
    }
    await saveGrup([...grupIds, id]);
    setArama('');
    setAramaSonuclari([]);
    setAramaAcik(false);
  };

  const handleRemove = async (id) => {
    await saveGrup(grupIds.filter((x) => x !== id));
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = arama.trim();
    if (q.length < 2) {
      setAramaSonuclari([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchTechServices(q, { aktif: true, limit: 20 });
        const list = res?.veri?.teknikServisler || [];
        setAramaSonuclari(Array.isArray(list) ? list : []);
        setAramaAcik(true);
      } catch (_e) {
        setAramaSonuclari([]);
      }
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [arama]);

  const handleOlustur = async () => {
    const ok = await confirm({
      title: 'Teknik servis kaydı oluştur',
      message: `${grup.length} teknik servis için Test Customer kurulum kaydı açılacak.`,
      confirmText: 'Oluştur',
      cancelText: 'Vazgeç',
    });
    if (!ok) return;

    setRunning(true);
    setError('');
    setSonuclar([]);
    try {
      const res = await runEgitimTsKayitOlustur();
      const veri = res?.veri || {};
      setSonuclar(veri.sonuclar || []);
      if (!res?.basarili && !(veri.sonuclar || []).length) {
        setError(res?.mesaj || 'Kayıtlar oluşturulamadı');
      }
    } catch (e) {
      setError(getErrorMessage(e, 'Kayıtlar oluşturulamadı'));
      const veri = e?.data?.veri;
      if (veri?.sonuclar?.length) setSonuclar(veri.sonuclar);
    } finally {
      setRunning(false);
    }
  };

  const checklist = durum?.checklist;

  return (
    <div className="page-container egitim-ts-kayit-page">
      <div className="page-header">
        <h1>Eğitim TS kayıtları</h1>
      </div>

      {error ? <div className="egitim-ts-alert egitim-ts-alert-error">{error}</div> : null}

      <section className="egitim-ts-card">
        <h2>Grup</h2>
        <div className="egitim-ts-search">
          <input
            type="search"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            onFocus={() => aramaSonuclari.length && setAramaAcik(true)}
            onBlur={() => setTimeout(() => setAramaAcik(false), 150)}
            placeholder="Teknik servis ara"
            disabled={saving || running}
          />
          {aramaAcik && aramaSonuclari.length > 0 ? (
            <div className="egitim-ts-search-results">
              {aramaSonuclari.map((ts) => {
                const id = tsId(ts);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleAdd(ts)}
                    disabled={grupIds.includes(id) || saving}
                  >
                    {tsLabel(ts)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="egitim-ts-muted">Yükleniyor…</p>
        ) : grup.length === 0 ? (
          <p className="egitim-ts-muted">Grupta teknik servis yok.</p>
        ) : (
          <ul className="egitim-ts-member-list">
            {grup.map((ts) => (
              <li key={tsId(ts)}>
                <span>
                  {decodeHtml(ts.ad || '')}
                  {ts.kod != null ? <code>#{ts.kod}</code> : null}
                </span>
                <button
                  type="button"
                  className="egitim-ts-remove"
                  onClick={() => handleRemove(tsId(ts))}
                  disabled={saving || running}
                >
                  Çıkar
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="egitim-ts-card">
        <h2>Kurulum kaydı</h2>
        {loading ? (
          <p className="egitim-ts-muted">Yükleniyor…</p>
        ) : (
          <ul className="egitim-ts-summary">
            <li>
              <strong>Müşteri</strong>
              <span>{durum?.musteri?.adSoyad || '—'}</span>
            </li>
            <li>
              <strong>Adres</strong>
              <span>{durum?.adres?.adres || '—'}</span>
            </li>
            <li>
              <strong>Ürün</strong>
              <span>
                {durum?.urun?.ad || '—'}
                {durum?.urun?.kod ? ` · ${durum.urun.kod}` : ''}
              </span>
            </li>
            <li>
              <strong>Kontrol listesi</strong>
              {checklist?.cevaplar?.length ? (
                <ul className="egitim-ts-checklist">
                  {checklist.cevaplar.map((c) => (
                    <li key={c.soruMetni}>
                      {c.soruMetni} — Evet
                      {c.deger ? ` (${c.deger})` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <span>—</span>
              )}
            </li>
          </ul>
        )}

        <div className="egitim-ts-actions">
          <button
            type="button"
            className="primary-btn"
            onClick={handleOlustur}
            disabled={loading || running || saving || grup.length === 0}
          >
            {running ? 'Oluşturuluyor…' : 'Teknik servis kaydı oluştur'}
          </button>
        </div>
      </section>

      {sonuclar.length > 0 ? (
        <section className="egitim-ts-card">
          <h2>Sonuç</h2>
          <table className="egitim-ts-results">
            <thead>
              <tr>
                <th>Teknik servis</th>
                <th>Durum</th>
                <th>SK</th>
              </tr>
            </thead>
            <tbody>
              {sonuclar.map((row) => (
                <tr key={row.teknikServisId}>
                  <td>{row.teknikServisAd}</td>
                  <td className={`egitim-ts-status-${row.durum}`}>
                    {durumLabel(row.durum)}
                    {row.durum === 'hata' && row.mesaj ? ` — ${row.mesaj}` : ''}
                  </td>
                  <td>{row.servisKaydiNo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
};

export default EgitimTsKayitPage;
